import { createPatientAccount } from './authApi';
import { revisePlan } from '../utils/planRevision';
import { get, query, ref, orderByChild, equalTo, limitToFirst, push, set, serverTimestamp, remove, update as dataUpdate, runTransaction } from './phpDataAdapter';
import { database } from '../config/backend';
import {
  Paciente,
  Plano,
  Consulta,
  Conversa,
  MensagemChat,
} from './mockData';
import { getMaxClients } from './subscriptionService';
import { MealPlanNutrition } from '../types/mealPlan';

// Utilidades internas
function safeString(value: any, fallback = ''): string {
  if (value == null) return fallback;
  return String(value);
}

function safeDateIso(value: any): string {
  try {
    if (!value) return new Date().toISOString();
    if (typeof value === 'string') {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d.toISOString();
    }
    if (typeof value === 'number') {
      return new Date(value).toISOString();
    }
    if (typeof value === 'object' && typeof value.seconds === 'number') {
      return new Date(value.seconds * 1000).toISOString();
    }
  } catch {
    // ignore
  }
  return new Date().toISOString();
}

// ===== Pacientes =====

export async function getPacientesForNutritionist(
  nutritionistId: string
): Promise<Paciente[]> {
  if (!nutritionistId) return [];

  try {
    // Fallback: buscar tudo e filtrar no cliente para evitar problemas de índice
    const relSnap = await get(ref(database, 'relationships'));
    if (!relSnap.exists()) {
      return [];
    }

    const allRels = relSnap.val() as Record<string, any>;
    const relationships = Object.values(allRels).filter(
      (r: any) => r.nutritionistId === nutritionistId
    );

    const patientIds = relationships.map((r: any) => r.patientId as string);

    const uniquePatientIds = Array.from(new Set(patientIds));

    const results: Paciente[] = [];


    await Promise.all(
      uniquePatientIds.map(async (patientId) => {
        try {
          const [userSnap, patientSnap, consultSnap] = await Promise.all([
            get(ref(database, `users/${patientId}`)),
            get(ref(database, `patients/${patientId}`)),
            get(ref(database, `users/${patientId}/consultations`)),
          ]);

          if (!userSnap.exists()) {
            return;
          }

          const user = userSnap.val() as any;
          const patientData = patientSnap.exists() ? (patientSnap.val() as any) : {};

          // Buscar última consulta sem usar query ordenada (para evitar erro de índice)
          let ultimaConsulta = '';
          if (consultSnap.exists()) {
            const consultas = consultSnap.val() as Record<string, any>;
            // Ordenar no cliente para encontrar a mais recente
            const consultasArray = Object.entries(consultas).map(([id, data]) => ({
              id,
              ...(data as any),
            }));

            // Ordenar por data (mais recente primeiro)
            consultasArray.sort((a, b) => {
              const dateA = new Date(a.date || a.createdAt || a.timestamp || 0).getTime();
              const dateB = new Date(b.date || b.createdAt || b.timestamp || 0).getTime();
              return dateB - dateA;
            });

            if (consultasArray.length > 0) {
              const maisRecente = consultasArray[0];
              ultimaConsulta = safeDateIso(maisRecente.date ?? maisRecente.createdAt ?? maisRecente.timestamp);
            }
          }

          const status: Paciente['status'] =
            (patientData.status as Paciente['status']) ?? 'ativo';

          results.push({
            id: patientId,
            nome: safeString(user.name || patientData.name, 'Paciente'),
            email: safeString(user.email, ''),
            objetivo: safeString(patientData.goal || patientData.objective, ''),
            ultimaConsulta,
            status,
          });
        } catch (error) {
          console.error(`Erro ao processar paciente ${patientId}:`, error);
          // ignora paciente com erro
        }
      })
    );

    return results;
  } catch (error) {
    console.error('Erro ao buscar pacientes:', error);
    return [];
  }
}

// Função auxiliar para gerar chave de data no formato YYYY-MM-DD
function getDateKey(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// ===== Pacientes com Baixa Adesão =====

export async function getPacientesComBaixaAdesao(
  nutritionistId: string
): Promise<Paciente[]> {
  if (!nutritionistId) return [];

  try {
    // Buscar todos os pacientes do nutricionista
    const pacientes = await getPacientesForNutritionist(nutritionistId);
    if (pacientes.length === 0) return [];

    const pacientesComAlerta: Paciente[] = [];
    const hoje = new Date();
    const diasVerificacao = 7; // Verificar últimos 7 dias

    await Promise.all(
      pacientes.map(async (paciente) => {
        try {
          let temBaixaAdesao = false;
          let scoreMaisRecente: number | null = null;
          let dataMaisRecente: string | null = null;

          // Verificar os últimos 7 dias
          for (let i = 0; i < diasVerificacao; i++) {
            const data = new Date(hoje);
            data.setDate(data.getDate() - i);
            const dateKey = getDateKey(data);

            try {
              const scoreSnap = await get(
                ref(database, `users/${paciente.id}/dietTracking/${dateKey}/dailyScore`)
              );

              if (scoreSnap.exists()) {
                const score = Number(scoreSnap.val()) || 0;
                if (scoreMaisRecente === null || dateKey > (dataMaisRecente || '')) {
                  scoreMaisRecente = score;
                  dataMaisRecente = dateKey;
                }
              }
            } catch {
              // Ignora erros de leitura de um dia específico
            }
          }

          // Ausência de registros não representa descumprimento do plano.
          if (scoreMaisRecente !== null && scoreMaisRecente < 70) {
            temBaixaAdesao = true;
          }

          if (temBaixaAdesao) {
            pacientesComAlerta.push(paciente);
          }
        } catch {
          // Uma falha de leitura também não comprova baixa adesão.
        }
      })
    );

    return pacientesComAlerta;
  } catch (error) {
    console.error('Erro ao buscar pacientes com baixa adesão:', error);
    return [];
  }
}

// ===== Planos =====

export async function getPlanosRecentesForNutritionist(
  nutritionistId: string
): Promise<Plano[]> {
  if (!nutritionistId) return [];

  // Estrutura de planos: mealPlans/{patientId}/* conforme app mobile
  const relQuery = query(
    ref(database, 'relationships'),
    orderByChild('nutritionistId'),
    equalTo(nutritionistId)
  );

  const relSnap = await get(relQuery);
  if (!relSnap.exists()) return [];

  const relationships = relSnap.val() as Record<string, any>;
  const patientIds = Array.from(
    new Set(Object.values(relationships).map((r: any) => r.patientId as string))
  );

  const planos: Plano[] = [];

  await Promise.all(
    patientIds.map(async (patientId) => {
      try {
        const plansSnap = await get(ref(database, `mealPlans/${patientId}`));
        if (!plansSnap.exists()) return;
        const obj = plansSnap.val() as Record<string, any>;

        Object.entries(obj).forEach(([planId, data]) => {
          const d = data as any;
          planos.push({
            id: safeString(planId),
            pacienteId: patientId,
            nome: safeString(d.name || d.title || d.planName, 'Plano'),
            duracaoSemanas: Number(d.durationWeeks || d.duration || 0),
            atualizadoEm: safeDateIso(d.updatedAt ?? d.createdAt),
          });
        });
      } catch {
        // ignora erro por paciente
      }
    })
  );

  // ordenar por atualizadoEm desc e limitar, por exemplo, aos 20 mais recentes
  planos.sort(
    (a, b) =>
      new Date(b.atualizadoEm).getTime() - new Date(a.atualizadoEm).getTime()
  );

  return planos.slice(0, 20);
}


// ===== Consultas =====

export async function getConsultasProximosDias(
  nutritionistId: string,
  diasJanela: number = 7
): Promise<Consulta[]> {
  if (!nutritionistId) return [];

  const now = new Date();
  const cutoff = new Date(now.getTime() + diasJanela * 24 * 60 * 60 * 1000);

  const relQuery = query(
    ref(database, 'relationships'),
    orderByChild('nutritionistId'),
    equalTo(nutritionistId)
  );
  const relSnap = await get(relQuery);
  if (!relSnap.exists()) return [];

  const relationships = relSnap.val() as Record<string, any>;
  const patientIds = Array.from(
    new Set(Object.values(relationships).map((r: any) => r.patientId as string))
  );

  const consultas: Consulta[] = [];

  await Promise.all(
    patientIds.map(async (patientId) => {
      try {
        // Buscar nome do paciente uma vez para todas as consultas
        let pacienteNome = 'Paciente';
        try {
          const userSnap = await get(ref(database, `users/${patientId}`));
          if (userSnap.exists()) {
            const userData = userSnap.val() as any;
            pacienteNome = safeString(userData.name, 'Paciente');
          }
        } catch {
          // Se não conseguir buscar, usa o fallback
        }

        const consultSnap = await get(
          ref(database, `users/${patientId}/consultations`)
        );
        if (!consultSnap.exists()) return;

        const obj = consultSnap.val() as Record<string, any>;
        await Promise.all(
          Object.entries(obj).map(async ([id, data]) => {
            const d = data as any;
            const date =
              d.nextAppointment ||
              d.date ||
              d.timestamp ||
              d.createdAt;
            const dt = new Date(date);
            if (isNaN(dt.getTime())) return;

            if (dt >= now && dt <= cutoff) {
              const tipo: Consulta['tipo'] =
                d.type === 'initial'
                  ? 'primeira'
                  : 'retorno';

              const status: Consulta['status'] =
                (d.status as Consulta['status']) ?? 'confirmada';

              // Usar patientName da consulta se existir, senão usar o nome buscado do usuário
              const nomeFinal = safeString(d.patientName, pacienteNome);

              // Buscar dados financeiros
              let valor: number | undefined;
              let pago: boolean | undefined;
              let dataPagamento: string | undefined;

              try {
                const financialRef = ref(
                  database,
                  `users/${nutritionistId}/consultations/${patientId}/${id}/financial`
                );
                const financialSnap = await get(financialRef);
                if (financialSnap.exists()) {
                  const financial = financialSnap.val();
                  valor = financial.valor ? Number(financial.valor) : undefined;
                  pago = financial.pago === true ? true : undefined;
                  dataPagamento = financial.dataPagamento || undefined;
                }
              } catch {
                // Ignora erro ao buscar dados financeiros
              }

              consultas.push({
                id,
                pacienteId: patientId,
                pacienteNome: nomeFinal,
                dataIso: dt.toISOString(),
                tipo,
                status,
                notes: d.notes ? safeString(d.notes) : undefined,
                valor,
                pago,
                dataPagamento,
              });
            }
          })
        );
      } catch {
        // ignora paciente com erro
      }
    })
  );

  // ordenar por data
  consultas.sort(
    (a, b) =>
      new Date(a.dataIso).getTime() - new Date(b.dataIso).getTime()
  );

  return consultas;
}

export async function getConsultasComDadosFinanceiros(
  nutritionistId: string,
  startDate?: Date,
  endDate?: Date
): Promise<Consulta[]> {
  if (!nutritionistId) return [];

  const relQuery = query(
    ref(database, 'relationships'),
    orderByChild('nutritionistId'),
    equalTo(nutritionistId)
  );
  const relSnap = await get(relQuery);
  if (!relSnap.exists()) return [];

  const relationships = relSnap.val() as Record<string, any>;
  const patientIds = Array.from(
    new Set(Object.values(relationships).map((r: any) => r.patientId as string))
  );

  const consultas: Consulta[] = [];

  await Promise.all(
    patientIds.map(async (patientId) => {
      try {
        let pacienteNome = 'Paciente';
        try {
          const userSnap = await get(ref(database, `users/${patientId}`));
          if (userSnap.exists()) {
            const userData = userSnap.val() as any;
            pacienteNome = safeString(userData.name, 'Paciente');
          }
        } catch {
          // Ignora erro
        }

        const consultSnap = await get(
          ref(database, `users/${patientId}/consultations`)
        );
        if (!consultSnap.exists()) return;

        const obj = consultSnap.val() as Record<string, any>;
        await Promise.all(
          Object.entries(obj).map(async ([id, data]) => {
            const d = data as any;
            const date =
              d.nextAppointment ||
              d.date ||
              d.timestamp ||
              d.createdAt;
            const dt = new Date(date);
            if (isNaN(dt.getTime())) return;

            // Filtrar por período se especificado
            if (startDate && dt < startDate) return;
            if (endDate && dt > endDate) return;

            // Buscar dados financeiros
            let valor: number | undefined;
            let pago: boolean | undefined;
            let dataPagamento: string | undefined;

            try {
              const financialRef = ref(
                database,
                `users/${nutritionistId}/consultations/${patientId}/${id}/financial`
              );
              const financialSnap = await get(financialRef);
              if (financialSnap.exists()) {
                const financial = financialSnap.val();
                valor = financial.valor ? Number(financial.valor) : undefined;
                pago = financial.pago === true ? true : undefined;
                dataPagamento = financial.dataPagamento || undefined;
              }
            } catch {
              // Ignora erro
            }

            // Só incluir se tiver dados financeiros
            if (valor === undefined && pago === undefined) return;

            const tipo: Consulta['tipo'] =
              d.type === 'initial'
                ? 'primeira'
                : 'retorno';

            const status: Consulta['status'] =
              (d.status as Consulta['status']) ?? 'confirmada';

            const nomeFinal = safeString(d.patientName, pacienteNome);

            consultas.push({
              id,
              pacienteId: patientId,
              pacienteNome: nomeFinal,
              dataIso: dt.toISOString(),
              tipo,
              status,
              notes: d.notes ? safeString(d.notes) : undefined,
              valor,
              pago,
              dataPagamento,
            });
          })
        );
      } catch {
        // Ignora erro
      }
    })
  );

  // Ordenar por data (mais recente primeiro)
  consultas.sort(
    (a, b) =>
      new Date(b.dataIso).getTime() - new Date(a.dataIso).getTime()
  );

  return consultas;
}

// ===== Chat =====

export async function getConversasChat(
  nutritionistId: string
): Promise<Conversa[]> {
  if (!nutritionistId) return [];

  const relQuery = query(
    ref(database, 'relationships'),
    orderByChild('nutritionistId'),
    equalTo(nutritionistId)
  );
  const relSnap = await get(relQuery);
  if (!relSnap.exists()) return [];

  const relationships = relSnap.val() as Record<string, any>;
  const patientIds = Array.from(
    new Set(Object.values(relationships).map((r: any) => r.patientId as string))
  );

  const conversas: Conversa[] = [];

  await Promise.all(
    patientIds.map(async (patientId) => {
      try {
        const userSnap = await get(ref(database, `users/${patientId}`));
        const user = userSnap.exists() ? (userSnap.val() as any) : {};
        const conversaId =
          nutritionistId < patientId
            ? `${nutritionistId}_${patientId}`
            : `${patientId}_${nutritionistId}`;

        const convSnap = await get(ref(database, `conversations/${conversaId}`));
        const conv = convSnap.exists() ? (convSnap.val() as any) : {};

        conversas.push({
          id: conversaId,
          pacienteNome: safeString(user.name, 'Paciente'),
          resumo: safeString(conv.lastMessage, 'Nenhuma mensagem ainda'),
        });
      } catch {
        // ignora
      }
    })
  );

  return conversas;
}

function formatChatTime(value: any): string {
  if (!value) return '';

  try {
    let date: Date;

    // Se for data antiga (objeto com seconds)
    if (typeof value === 'object' && value.seconds) {
      date = new Date(value.seconds * 1000);
    }
    // Se for string ISO
    else if (typeof value === 'string') {
      date = new Date(value);
    }
    // Se for número (timestamp em ms)
    else if (typeof value === 'number') {
      date = new Date(value);
    }
    else {
      return '';
    }

    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((today.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24));

    // Se for hoje, mostra apenas hora
    if (diffDays === 0) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
    // Se for ontem
    else if (diffDays === 1) {
      return `Ontem ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    }
    // Se for esta semana
    else if (diffDays < 7) {
      const days = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
      return `${days[date.getDay()]} ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    }
    // Mais antigo
    else {
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  } catch {
    return '';
  }
}

export function parseMensagensChat(obj: Record<string, any> | null | undefined, conversaId: string): MensagemChat[] {
  if (!obj || !conversaId) return [];
  const mensagens: Array<MensagemChat & { rawTimestamp: number }> = [];

  Object.entries(obj).forEach(([id, data]) => {
    try {
      const d = data as any;
      const timestamp = d.createdAt ?? d.timestamp ?? d.time ?? '';

      // Converter timestamp para número para ordenação
      let rawTimestamp = 0;
      if (typeof timestamp === 'object' && timestamp.seconds) {
        rawTimestamp = timestamp.seconds * 1000;
      } else if (typeof timestamp === 'string') {
        rawTimestamp = new Date(timestamp).getTime();
      } else if (typeof timestamp === 'number') {
        rawTimestamp = timestamp;
      }

      mensagens.push({
        id,
        conversaId,
        autor: ['nutritionist', 'nutri'].includes(String(d.author || d.senderRole || '').toLowerCase())
          ? 'nutri'
          : 'paciente',
        conteudo: safeString(d.text ?? d.message ?? d.content, ''),
        horario: formatChatTime(timestamp),
        rawTimestamp,
        audio: d.audio || undefined,
        imagem: d.image || undefined,
        image: d.image || undefined,
        video: d.video || undefined,
        videoUrl: d.videoUrl || undefined,
        videoThumbnail: d.videoThumbnail || undefined,
        type: d.type || undefined,
        pdfId: d.pdfId || undefined,
        pdfUrl: d.pdfUrl || undefined,
        pdfFileName: d.pdfFileName || undefined,
      });
    } catch (error) {
      console.warn(`Erro ao processar mensagem ${id}:`, error);
      // Ignorar mensagens com erro e continuar processando as outras
    }
  });

  // Ordenar por timestamp (mais antigas primeiro para exibição correta)
  mensagens.sort((a, b) => a.rawTimestamp - b.rawTimestamp);

  // Remover rawTimestamp antes de retornar
  return mensagens.map(({ rawTimestamp, ...msg }) => msg);
}

export async function getMensagensChat(conversaId: string): Promise<MensagemChat[]> {
  if (!conversaId) return [];
  const snap = await get(ref(database, `messages/${conversaId}`));
  return snap.exists() ? parseMensagensChat(snap.val(), conversaId) : [];
}

/**
 * Extrai o patientId de um conversationId
 * O conversationId é no formato "uid1_uid2" (ordenado alfabeticamente)
 */
export function extractPatientIdFromConversationId(
  conversationId: string,
  nutritionistId: string
): string | null {
  if (!conversationId || !nutritionistId) return null;

  if (conversationId.startsWith(`${nutritionistId}_`)) return conversationId.slice(nutritionistId.length + 1) || null;
  if (conversationId.endsWith(`_${nutritionistId}`)) return conversationId.slice(0, -nutritionistId.length - 1) || null;
  return null;
}

export async function enviarMensagemChat(
  conversaId: string,
  author: 'nutritionist' | 'patient',
  text: string,
  audioUrl?: string,
  imageUrl?: string,
  videoUrl?: string,
  videoThumbnailUrl?: string
): Promise<void> {
  if (!conversaId || (!text.trim() && !audioUrl && !imageUrl && !videoUrl)) return;

  const messagesRef = ref(database, `messages/${conversaId}`);
  const newMsgRef = push(messagesRef);
  const messageData: any = {
    author,
    text: text || '',
    createdAt: serverTimestamp(),
  };

  if (audioUrl) {
    messageData.audio = audioUrl;
  }

  if (imageUrl) {
    messageData.image = imageUrl;
  }

  await set(newMsgRef, messageData);
}

// ===== Escrita (Novas Funções) =====

export async function createPatient(
  nutritionistId: string,
  data: {
    nome: string;
    email: string;
    objetivo?: string;
    cpf?: string;
    rg?: string;
    birthDate?: string;
    gender?: 'male' | 'female';
    bloodType?: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    height?: string;
    weight?: string;
    activityLevel?: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
    goals?: string[];
    category?: 'ativo' | 'avaliacao' | 'alerta';
    medicalConditions?: string;
    allergies?: string;
    medications?: string;
    supplements?: string;
    initialObservations?: string;
    gamification?: {
      enabled: boolean;
      sealThreshold: number;
      specialRewardThreshold: number;
      specialRewardText: string;
      unlockExerciseThreshold: number;
    };
  },
  password: string,
  createUserForNutritionist?: (email: string, password: string, name: string, userData: any) => Promise<string>
): Promise<void> {
  if (!nutritionistId) throw new Error('Nutricionista não identificado');
  if (!data.nome || !data.email) throw new Error('Nome e email são obrigatórios');

  // Verificar limite de pacientes
  const maxClients = await getMaxClients(nutritionistId);
  const currentPatients = await getPacientesForNutritionist(nutritionistId);

  // Se maxClients for muito alto (9999), consideramos ilimitado
  if (currentPatients.length >= maxClients && maxClients < 9000) {
    throw new Error(`Limite de pacientes atingido para seu plano atual (${maxClients}). Faça um upgrade para adicionar mais pacientes.`);
  }

  // Calcular idade a partir da data de nascimento
  const calculateAge = (birthDate: string): number | null => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const age = data.birthDate ? calculateAge(data.birthDate) : null;

  const userData = {
    age,
    height: data.height ? parseFloat(data.height) : null,
    weight: data.weight ? parseFloat(data.weight) : null,
    gender: data.gender || 'male',
    activityLevel: data.activityLevel || 'moderate',
    goal: data.goals && data.goals.length > 0 ? data.goals[0] : (data.objetivo || 'weight_loss'),
    medicalConditions: data.medicalConditions || null,
    allergies: data.allergies || null,
    medications: data.medications || null,
    personalData: {
      cpf: data.cpf || null,
      rg: data.rg || null,
      birthDate: data.birthDate || null,
      gender: data.gender || 'male',
      bloodType: data.bloodType || null,
      phone: data.phone || null,
      address: data.address || null,
      city: data.city || null,
      state: data.state || null,
      zipCode: data.zipCode || null,
    },
    goals: data.goals || (data.objetivo ? [data.objetivo] : []),
    category: data.category || 'ativo',
    supplements: data.supplements || null,
    initialObservations: data.initialObservations || null,
    gamification: data.gamification || null,
  };

  if (!password || password.length < 10) throw new Error('A senha deve ter pelo menos 10 caracteres.');
  const createAccount = createUserForNutritionist || createPatientAccount;
  // SQL creates the account, both profiles and the relationship atomically.
  await createAccount(data.email, password, data.nome, userData);
}

export async function createConsultation(
  nutritionistId: string,
  patientId: string,
  data: {
    dataIso: string;
    tipo: 'primeira' | 'retorno';
    status: 'confirmada' | 'aguardando';
    notes?: string;
    valor?: number;
    pago?: boolean;
    dataPagamento?: string;
  }
): Promise<void> {
  if (!nutritionistId || !patientId) throw new Error('Dados inválidos');

  const consultsRef = ref(database, `users/${patientId}/consultations`);
  const newConsultRef = push(consultsRef);
  const consultationId = newConsultRef.key!;

  const consultationData: any = {
    date: data.dataIso,
    type: data.tipo === 'primeira' ? 'initial' : 'return',
    status: data.status,
    nutritionistId,
    createdAt: serverTimestamp(),
  };

  if (data.notes) {
    consultationData.notes = data.notes;
  }

  await set(newConsultRef, consultationData);

  // Salvar dados financeiros se fornecidos
  if (data.valor !== undefined || data.pago !== undefined || data.dataPagamento !== undefined) {
    const financialRef = ref(
      database,
      `users/${nutritionistId}/consultations/${patientId}/${consultationId}/financial`
    );

    const financialData: any = {};
    if (data.valor !== undefined) financialData.valor = data.valor;
    if (data.pago !== undefined) financialData.pago = data.pago;
    if (data.dataPagamento !== undefined) {
      financialData.dataPagamento = data.dataPagamento || null;
    }
    financialData.updatedAt = new Date().toISOString();

    await set(financialRef, financialData);
  }
}

export async function createPlan(
  nutritionistId: string,
  patientId: string,
  data: {
    revision?: number;
    nome: string;
    objetivo?: string;
    observacoes?: string;
    targetCalories?: number;
    protein?: number;
    carbohydrates?: number;
    fats?: number;
    weeklyPlan?: any;
    foodNutrients?: MealPlanNutrition;
    hydration?: string;
    substitutions?: any;
    bmr?: number;
    tdee?: number;
    durationWeeks?: number;
  }
): Promise<void> {
  if (!nutritionistId || !patientId) throw new Error('Dados inválidos');

  const plansRef = ref(database, `mealPlans/${patientId}`);
  const newPlanRef = push(plansRef);

  await set(newPlanRef, {
    revision: 1,
    title: data.nome,
    name: data.nome,
    goal: data.objetivo || '',
    notes: data.observacoes || '',
    targetCalories: data.targetCalories ?? null,
    protein: data.protein ?? null,
    carbohydrates: data.carbohydrates ?? null,
    fats: data.fats ?? null,
    weeklyPlan: data.weeklyPlan || null,
    foodNutrients: data.foodNutrients || null,
    hydration: data.hydration || '2L/dia',
    substitutions: data.substitutions || null,
    bmr: data.bmr || null,
    tdee: data.tdee || null,
    status: 'active',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    nutritionistId,
    patientId,
    durationWeeks: data.durationWeeks ?? null,
  });
}

export async function updatePlan(
  nutritionistId: string,
  patientId: string,
  planId: string,
  data: Parameters<typeof createPlan>[2]
): Promise<void> {
  if (!nutritionistId || !patientId || !planId || !data.nome.trim()) throw new Error('Dados do plano inválidos');
  const planRef = ref(database, `mealPlans/${patientId}/${planId}`);
  const snapshot = await get(planRef);
  if (!snapshot.exists()) throw new Error('Plano não encontrado');
  const existing = snapshot.val();
  if (existing.nutritionistId && existing.nutritionistId !== nutritionistId) throw new Error('Plano de outro nutricionista');
  const relationships = await get(query(ref(database, 'relationships'), orderByChild('nutritionistId'), equalTo(nutritionistId)));
  if (!Object.values(relationships.val() || {}).some((relationship: any) => relationship.patientId === patientId)) {
    throw new Error('Paciente não vinculado ao nutricionista');
  }
  const changes = {
    name: data.nome.trim(), title: data.nome.trim(), goal: data.objetivo || '', notes: data.observacoes || '',
    targetCalories: data.targetCalories ?? null, protein: data.protein ?? null,
    carbohydrates: data.carbohydrates ?? null, fats: data.fats ?? null,
    weeklyPlan: data.weeklyPlan || null, hydration: data.hydration || '2L/dia',
    foodNutrients: data.foodNutrients || null,
    substitutions: data.substitutions ?? null, bmr: data.bmr ?? null, tdee: data.tdee ?? null,
    durationWeeks: data.durationWeeks ?? existing.durationWeeks ?? null,
    updatedAt: serverTimestamp(), nutritionistId, patientId,
  };
  const result = await runTransaction(planRef, current => {
    if (!current) return; // O SDK pode tentar primeiro com cache vazio.
    return revisePlan(current, changes, data.revision ?? 0, nutritionistId, serverTimestamp());
  }, { applyLocally: false });
  if (!result.committed) throw new Error('O plano não pôde ser atualizado. Atualize a lista e tente novamente.');
}

// ===== Atualizar Paciente =====

export async function updatePatient(
  nutritionistId: string,
  patientId: string,
  data: {
    nome: string;
    email: string;
    objetivo?: string;
    cpf?: string;
    rg?: string;
    birthDate?: string;
    gender?: 'male' | 'female';
    bloodType?: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    height?: string;
    weight?: string;
    activityLevel?: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
    goals?: string[];
    category?: 'ativo' | 'avaliacao' | 'alerta';
    medicalConditions?: string;
    allergies?: string;
    medications?: string;
    supplements?: string;
    initialObservations?: string;
    gamification?: {
      enabled: boolean;
      sealThreshold: number;
      specialRewardThreshold: number;
      specialRewardText: string;
      unlockExerciseThreshold: number;
    };
  }
): Promise<void> {
  if (!nutritionistId || !patientId) throw new Error('Dados inválidos');
  if (!data.nome || !data.email) throw new Error('Nome e email são obrigatórios');

  // Verificar se o nutricionista tem relacionamento com este paciente
  const relSnap = await get(ref(database, 'relationships'));
  if (relSnap.exists()) {
    const allRels = relSnap.val() as Record<string, any>;
    const hasRelationship = Object.values(allRels).some(
      (r: any) => r.nutritionistId === nutritionistId && r.patientId === patientId
    );
    if (!hasRelationship) {
      throw new Error('Você não tem permissão para editar este paciente');
    }
  }

  // Calcular idade a partir da data de nascimento
  const calculateAge = (birthDate: string): number | null => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const age = data.birthDate ? calculateAge(data.birthDate) : null;

  const userData: any = {
    name: data.nome,
    email: data.email,
    age,
    height: data.height ? parseFloat(data.height) : null,
    weight: data.weight ? parseFloat(data.weight) : null,
    gender: data.gender || 'male',
    activityLevel: data.activityLevel || 'moderate',
    goal: data.goals && data.goals.length > 0 ? data.goals[0] : (data.objetivo || ''),
    medicalConditions: data.medicalConditions || null,
    allergies: data.allergies || null,
    medications: data.medications || null,
    personalData: {
      cpf: data.cpf || null,
      rg: data.rg || null,
      birthDate: data.birthDate || null,
      gender: data.gender || 'male',
      bloodType: data.bloodType || null,
      phone: data.phone || null,
      address: data.address || null,
      city: data.city || null,
      state: data.state || null,
      zipCode: data.zipCode || null,
    },
    goals: data.goals || (data.objetivo ? [data.objetivo] : []),
    category: data.category || 'ativo',
    supplements: data.supplements || null,
    initialObservations: data.initialObservations || null,
    gamification: data.gamification || null,
  };

  // Atualizar dados em users/{patientId}
  await dataUpdate(ref(database, `users/${patientId}`), userData);

  // Atualizar dados em patients/{patientId}
  const patientData: any = {
    name: data.nome,
    goal: data.objetivo || (data.goals && data.goals.length > 0 ? data.goals[0] : ''),
    goals: data.goals || [],
    status: data.category || 'ativo',
    age,
    height: data.height ? parseFloat(data.height) : null,
    weight: data.weight ? parseFloat(data.weight) : null,
    gender: data.gender || 'male',
    activityLevel: data.activityLevel || 'moderate',
    medicalConditions: data.medicalConditions || null,
    allergies: data.allergies || null,
    medications: data.medications || null,
    gamification: data.gamification || null,
  };

  await dataUpdate(ref(database, `patients/${patientId}`), patientData);


}

// ===== Excluir Paciente =====

export async function deletePatient(
  nutritionistId: string,
  patientId: string
): Promise<void> {
  if (!nutritionistId || !patientId) throw new Error('Dados inválidos');

  // Verificar se o nutricionista tem relacionamento com este paciente
  const relSnap = await get(ref(database, 'relationships'));
  if (!relSnap.exists()) {
    throw new Error('Relacionamento não encontrado');
  }

  const allRels = relSnap.val() as Record<string, any>;
  let relationshipKey: string | null = null;

  for (const [key, rel] of Object.entries(allRels)) {
    if (rel.nutritionistId === nutritionistId && rel.patientId === patientId) {
      relationshipKey = key;
      break;
    }
  }

  if (!relationshipKey) {
    throw new Error('Você não tem permissão para excluir este paciente');
  }

  // Archive the link; keep the patient's account and clinical history.
  await remove(ref(database, `relationships/${relationshipKey}`));
}

// ===== Atualizar Valor de Consulta =====

export async function updateConsultationFinancial(
  nutritionistId: string,
  patientId: string,
  consultationId: string,
  data: {
    valor?: number;
    pago?: boolean;
    dataPagamento?: string;
  }
): Promise<void> {
  if (!nutritionistId || !patientId || !consultationId) {
    throw new Error('Dados inválidos');
  }

  const financialRef = ref(
    database,
    `users/${nutritionistId}/consultations/${patientId}/${consultationId}/financial`
  );

  const financialData: any = {};
  if (data.valor !== undefined) financialData.valor = data.valor;
  if (data.pago !== undefined) financialData.pago = data.pago;
  if (data.dataPagamento !== undefined) {
    financialData.dataPagamento = data.dataPagamento || null;
  }
  financialData.updatedAt = new Date().toISOString();

  await set(financialRef, financialData);
}
